import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BarChart3, Users, FileText, CreditCard, Search, Share2, Shield, Bot, Zap, Monitor, Globe, TrendingUp, CheckCircle, CalendarDays, QrCode, Tv, Film, Timer, LayoutList, Wifi, Mail, MapPin, Youtube, ExternalLink, Play } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const features = [
  { icon: Monitor, title: "Dashboard de gestion", desc: "Tableau de bord complet avec KPIs en temps reel. Suivez vos performances, revenus et activites depuis une interface unique.", color: "#0099CC" },
  { icon: Users, title: "Gestion clients", desc: "CRM integre pour gerer vos clients, contacts et interactions. Historique complet et fiches detaillees.", color: "#10B981" },
  { icon: FileText, title: "Devis & Contrats", desc: "Creez des propositions commerciales, generez des contrats PDF et signez electroniquement en quelques clics.", color: "#8B5CF6" },
  { icon: CreditCard, title: "Paiements en ligne", desc: "Facturation automatisee et paiements securises via Stripe. Suivi des encaissements et relances.", color: "#F59E0B" },
  { icon: Search, title: "SEO & Google Analytics", desc: "Connectez Google Analytics et Search Console. Suivez vos positions, trafic et performances SEO.", color: "#EF4444" },
  { icon: Share2, title: "Gestion reseaux sociaux", desc: "Planifiez et publiez sur Facebook, Instagram, LinkedIn et TikTok. Calendrier editorial et statistiques.", color: "#EC4899" },
  { icon: Shield, title: "Surveillance concurrents", desc: "Analysez les positions SEO de vos concurrents et identifiez les opportunites de croissance.", color: "#06B6D4" },
  { icon: Bot, title: "Assistant IA", desc: "Intelligence artificielle integree pour generer du contenu, analyser vos donnees et automatiser vos taches.", color: "#A855F7" },
];

const sectors = [
  { title: "Agences immobilieres", path: "/secteurs/agences-immobilieres", desc: "Gestion mandats, acquereurs et vitrine" },
  { title: "E-commerce", path: "/secteurs/e-commerce", desc: "Analytics, marketing et fidelisation" },
  { title: "Agences marketing", path: "/secteurs/agences-marketing", desc: "Multi-clients, reporting et campagnes" },
  { title: "Consultants & Services", path: "/secteurs/consultants", desc: "Propositions, contrats et suivi" },
];

const stats = [
  { val: "12+", label: "Modules integres" },
  { val: "4", label: "Secteurs couverts" },
  { val: "99.9%", label: "Disponibilite" },
  { val: "24/7", label: "Support IA" },
];

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateShort(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; }
}

function EventsMediaShowcase() {
  const [featured, setFeatured] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [featRes, evRes] = await Promise.all([
          axios.get(`${API}/api/showcase/featured`),
          axios.get(`${API}/api/showcase/events?limit=4`),
        ]);
        setFeatured(featRes.data);
        setEvents(evRes.data || []);
      } catch {}
    };
    load();
  }, []);

  const feat = featured?.event;

  return (
    <section className="py-24 bg-[#060B14]" data-testid="events-media-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EF4444]/10 border border-[#EF4444]/20 text-[#EF4444] text-xs font-semibold mb-4">
              <Play className="h-3 w-3" /> Evenements & Medias
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold">Vos evenements meritent d'etre vus</h2>
            <p className="text-[#94A3B8] mt-2 max-w-xl">Conferences, rencontres, reportages et diffusion audiovisuelle : decouvrez les evenements accompagnes par GLOTECH DIGITAL.</p>
          </div>
          <Link to="/evenements-medias" className="hidden md:inline-flex items-center gap-2 text-[#0099CC] hover:text-white font-medium text-sm transition-colors" data-testid="view-all-events">
            Tout voir <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Featured Banner */}
        {feat && (
          <div className="relative rounded-2xl overflow-hidden mb-10 group" data-testid="featured-banner">
            <div className="aspect-[21/9] bg-[#151B2B]">
              {feat.cover_image_url ? (
                <img src={feat.cover_image_url} alt={feat.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#0099CC]/20 to-[#F59E0B]/10 flex items-center justify-center">
                  <CalendarDays className="w-20 h-20 text-white/10" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-[10px] font-bold border border-[#F59E0B]/30 mb-3">A la une</span>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{feat.title}</h3>
                <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm mb-4">
                  {feat.event_date && <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-[#F59E0B]" />{formatDateShort(feat.event_date)}</span>}
                  {(feat.location || feat.city) && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#0099CC]" />{[feat.location, feat.city].filter(Boolean).join(", ")}</span>}
                  {feat.org_name && <span>par <strong className="text-white">{feat.org_name}</strong></span>}
                </div>
                {feat.org_slug && feat.slug && (
                  <Link to={`/e/${feat.org_slug}/${feat.slug}`} className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all" data-testid="featured-discover-btn">
                    Decouvrir l'evenement <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Events Grid (small cards) */}
        {events.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {events.slice(0, 4).map(ev => (
              <div key={ev.id} className="group bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden hover:border-[#0099CC]/40 transition-all" data-testid={`home-event-${ev.id}`}>
                <div className="aspect-[16/10] bg-[#0B1120] overflow-hidden">
                  {ev.cover_image_url ? (
                    <img src={ev.cover_image_url} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><CalendarDays className="w-8 h-8 text-[#2A3441]" /></div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="text-white font-semibold text-sm mb-1 line-clamp-2 group-hover:text-[#0099CC]">{ev.title}</h4>
                  {ev.event_date && <p className="text-[#64748B] text-xs flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDateShort(ev.event_date)}</p>}
                  {ev.org_name && <p className="text-[#4A5568] text-xs mt-1">par {ev.org_name}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Social + View All */}
        <div className="flex items-center justify-between pt-4 border-t border-[#1A2332]">
          <div className="flex items-center gap-3">
            <span className="text-[#64748B] text-sm">Suivez-nous</span>
            <a href="https://www.youtube.com/@GlotechDigital" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/20 transition-all" data-testid="home-yt">
              <Youtube className="w-4 h-4" />
            </a>
            <a href="https://www.tiktok.com/@glotechdigital" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-all" data-testid="home-tt">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.11V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>
            </a>
          </div>
          <Link to="/evenements-medias" className="md:hidden inline-flex items-center gap-2 text-[#0099CC] hover:text-white font-medium text-sm transition-colors">
            Tout voir <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function PublicHomePage() {
  usePageTitle("Agence Digitale & Evenementiel");
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0099CC]/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0099CC]/10 border border-[#0099CC]/20 text-[#0099CC] text-xs font-semibold mb-6">
              <Zap className="h-3.5 w-3.5" /> Plateforme SaaS tout-en-un
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6">
              Gerez et developpez votre activite avec{" "}
              <span className="text-[#0099CC]">GLOTECH OS</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl">
              CRM, facturation, SEO, reseaux sociaux, contrats, paiements et intelligence artificielle reunis dans une seule plateforme. Pour les agences, PME, consultants et entrepreneurs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/audit" className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#0d9668] text-white px-7 py-3.5 rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-[#10B981]/20" data-testid="hero-cta-audit">
                Audit SEO gratuit <Search className="h-4 w-4" />
              </Link>
              <Link to="/devis" className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-7 py-3.5 rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-[#0099CC]/20" data-testid="hero-cta-demo">
                Demander une demo <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/plateforme" className="inline-flex items-center gap-2 border border-[#2A3441] text-white hover:border-[#94A3B8] px-7 py-3.5 rounded-xl font-semibold transition-colors">
                Decouvrir la plateforme
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20">
            {stats.map(s => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-bold text-white">{s.val}</div>
                <div className="text-sm text-[#64748b] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Tout ce dont vous avez besoin</h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto">Une suite complete d'outils pour digitaliser et automatiser votre activite professionnelle.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(f => (
            <div key={f.title} className="group bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 hover:border-[#0099CC]/30 transition-all hover:-translate-y-1" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${f.color}15` }}>
                <f.icon className="h-5 w-5" style={{ color: f.color }} />
              </div>
              <h3 className="text-white font-semibold mb-2">{f.title}</h3>
              <p className="text-[#64748b] text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== EVENT CENTER SHOWCASE ===== */}
      <section className="relative overflow-hidden bg-[#080C14] py-24" data-testid="event-center-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_#F59E0B08_0%,_transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="flex flex-col lg:flex-row items-start gap-16">
            {/* Left: Intro */}
            <div className="lg:w-2/5 lg:sticky lg:top-24">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-[#F59E0B] text-xs font-semibold mb-6">
                <CalendarDays className="h-3.5 w-3.5" /> Nouveau module
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
                GLOTECH <span className="text-[#F59E0B]">Event Center</span>
              </h2>
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                Organisez, gerez et diffusez vos evenements professionnels depuis une seule plateforme. De l'inscription a la regie de diffusion en direct, tout est integre.
              </p>
              <Link
                to="/devis"
                className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-black px-7 py-3.5 rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-[#F59E0B]/20"
                data-testid="event-center-cta"
              >
                <Mail className="h-4 w-4" /> Demander un acces <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Right: Features Grid */}
            <div className="lg:w-3/5 grid sm:grid-cols-2 gap-4">
              {[
                { icon: CalendarDays, title: "Gestion d'evenements", desc: "Creez, publiez et gerez vos evenements avec billetterie, statuts (brouillon, publie, termine, annule) et page d'inscription publique.", color: "#F59E0B" },
                { icon: QrCode, title: "QR Code & Check-in", desc: "QR code unique par inscrit. Scannez sur place pour le pointage des presences en temps reel.", color: "#10B981" },
                { icon: Tv, title: "Regie de diffusion Live", desc: "Pilotez un ecran public depuis votre dashboard : Preview, Program, transitions Cut/Fade et mode Direct.", color: "#0099CC" },
                { icon: Film, title: "Multi-sources", desc: "Diffusez des presentations PDF, videos YouTube, videos locales, textes, QR Codes et ecrans d'attente personnalises.", color: "#8B5CF6" },
                { icon: BarChart3, title: "Live Stats sur ecran", desc: "Affichez les statistiques de l'evenement en temps reel sur l'ecran public : inscrits, presents, taux de presence.", color: "#EF4444" },
                { icon: Timer, title: "Compte a rebours", desc: "Affichez un timer inversé configurable sur l'ecran public avant le debut de l'evenement.", color: "#EC4899" },
                { icon: Wifi, title: "Multi-ecrans simultanees", desc: "Connectez plusieurs ecrans de diffusion en meme temps. Compteur d'ecrans en temps reel dans la regie.", color: "#06B6D4" },
                { icon: LayoutList, title: "Conducteur & Scenes", desc: "Organizez vos scenes par drag & drop, exportez le conducteur en PDF imprimable pour votre equipe.", color: "#F97316" },
              ].map(f => (
                <div key={f.title} className="group bg-[#151B2B]/80 backdrop-blur-sm border border-[#2A3441] rounded-xl p-5 hover:border-[#F59E0B]/30 transition-all" data-testid={`ec-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${f.color}12` }}>
                      <f.icon className="h-4 w-4" style={{ color: f.color }} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm mb-1">{f.title}</h3>
                      <p className="text-[#64748b] text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== EVENEMENTS & MEDIAS SECTION ===== */}
      <EventsMediaShowcase />

      {/* Platform Preview */}
      <section className="bg-[#080C14] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Une plateforme, des possibilites infinies</h2>
              <p className="text-[#94A3B8] mb-8 leading-relaxed">GLOTECH OS centralise tous vos outils metier dans une interface unique et intuitive. Fini les dizaines d'abonnements et d'outils disperses.</p>
              <div className="space-y-4">
                {["Tunnel commercial complet : lead → devis → contrat → facture → projet", "Suivi SEO en temps reel avec Google Analytics et Search Console", "Publication automatisee sur les reseaux sociaux", "Intelligence artificielle pour la generation de contenu", "Dashboard KPI personnalise pour chaque client"].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-[#94A3B8] text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/fonctionnalites" className="inline-flex items-center gap-2 text-[#0099CC] hover:text-white mt-8 font-medium text-sm transition-colors">
                Voir toutes les fonctionnalites <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 space-y-4">
              {[
                { label: "Clients actifs", val: "127", pct: "+12%", color: "#10B981" },
                { label: "Revenus mensuels", val: "24,850 EUR", pct: "+8%", color: "#0099CC" },
                { label: "Taux de conversion", val: "68%", pct: "+5%", color: "#F59E0B" },
                { label: "Score SEO moyen", val: "87/100", pct: "+15%", color: "#8B5CF6" },
              ].map(m => (
                <div key={m.label} className="flex items-center justify-between py-3 border-b border-[#2A3441] last:border-0">
                  <span className="text-[#94A3B8] text-sm">{m.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-semibold">{m.val}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${m.color}15`, color: m.color }}>{m.pct}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Adapte a votre secteur</h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto">GLOTECH OS s'adapte a votre metier avec des fonctionnalites specifiques a chaque secteur d'activite.</p>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {sectors.map(s => (
            <Link key={s.path} to={s.path} className="group bg-[#151B2B] border border-[#2A3441] rounded-xl p-5 hover:border-[#0099CC]/30 transition-all text-center" data-testid={`sector-card-${s.path.split('/').pop()}`}>
              <Globe className="h-8 w-8 text-[#0099CC] mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="text-white font-semibold text-sm mb-1">{s.title}</h3>
              <p className="text-[#64748b] text-xs">{s.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* SEO Audit CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-2xl p-10 flex flex-col lg:flex-row items-center gap-10">
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-3">Audit SEO gratuit en 30 secondes</h2>
            <p className="text-[#94A3B8] mb-4">Analysez votre site web et decouvrez les points a ameliorer pour booster votre visibilite sur Google. C'est gratuit et sans engagement.</p>
            <div className="flex items-center gap-4 text-sm text-[#64748b]">
              <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-[#10B981]" />Diagnostic complet</span>
              <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-[#10B981]" />Rapport par email</span>
              <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-[#10B981]" />100% gratuit</span>
            </div>
          </div>
          <Link to="/audit" className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#0d9668] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all shrink-0" data-testid="cta-audit-btn">
            <Search className="h-5 w-5" />Lancer l'audit
          </Link>
        </div>
      </section>

      {/* Automated Funnel */}
      <section className="bg-[#080C14] py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Tunnel commercial automatise</h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto mb-12">De la generation de leads a la facturation, tout est automatise.</p>
          <div className="flex items-center justify-center gap-1 flex-wrap">
            {["Lead", "Proposition", "Signature", "Client", "Projet", "Facture", "Paiement"].map((s, i) => (
              <div key={i} className="flex items-center gap-1">
                {i > 0 && <ArrowRight className="h-4 w-4 text-[#64748b]" />}
                <span className="bg-[#0099CC]/10 text-[#0099CC] px-4 py-2 rounded-full text-sm font-medium">{s}</span>
              </div>
            ))}
          </div>
          <p className="text-[#64748b] text-sm mt-8">100% automatise. Zero intervention manuelle entre chaque etape.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#0099CC]/10 to-[#0077AA]/10 border-y border-[#0099CC]/20">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Pret a digitaliser votre activite ?</h2>
          <p className="text-[#94A3B8] mb-8 max-w-xl mx-auto">Demandez une demonstration personnalisee et decouvrez comment GLOTECH OS peut transformer votre entreprise.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:shadow-lg hover:shadow-[#0099CC]/20" data-testid="cta-demo-btn">
              Demander une demonstration <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
